import { computed, inject, Injectable, signal } from '@angular/core';
import { httpResource } from '@angular/common/http';

import { DemandReferenceService } from '../Services/demand-reference.service';
import { DemandReferenceResolveResponse } from '../../../Interfaces/api/catalog/demand-reference.interface';
import { DEMAND_REFERENCE_MISS_ROUTE_PATH } from '../Consts/demand-reference-route.const';
import { buildDemandReferenceDestination } from '../Shared/demand-reference-destination.util';

@Injectable({ providedIn: 'root' })
export class DemandReferenceFacade {
  private readonly _service = inject(DemandReferenceService);

  private readonly _reference = signal<string | null>(null);

  private readonly _resolution = httpResource<DemandReferenceResolveResponse>(() => {
    const reference = this._reference();
    return reference ? this._service.resolve(reference) : undefined;
  });

  readonly resolving = computed<boolean>(() => this._resolution.isLoading());

  // The API answers a miss with 404, 422 or a framework 404 by input shape, so any error is a miss.
  readonly missed = computed<boolean>(() => {
    const reference = this._reference();
    if (reference === null) return false;
    return reference === '' || this._resolution.status() === 'error';
  });

  // Not ResourceStateService: it returns the previous value while loading and would misroute.
  readonly destination = computed<string[] | null>(() =>
    this._resolution.hasValue()
      ? buildDemandReferenceDestination(this._resolution.value().data)
      : null,
  );

  readonly landing = computed<string[] | null>(() =>
    this.missed() ? [DEMAND_REFERENCE_MISS_ROUTE_PATH] : this.destination(),
  );

  resolve(reference: string): void {
    this._reference.set(reference.trim());
  }

  reset(): void {
    this._reference.set(null);
  }
}
